import React, { useState } from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaText from "../../ui-kit/typography/Text/Text";
import "./branching-modal.scss";

export interface CommitModalPayload {
  title: string;
  message: string;
  commitMessage: string;
}

const buildCommitMessage = (title: string, message: string): string => {
  const normalizedTitle = (title || "").trim();
  const normalizedMessage = (message || "").trim();
  if (!normalizedMessage) return normalizedTitle;
  return `${normalizedTitle}\n\n${normalizedMessage}`;
};

export interface ICommitToBranchModalProps {
  showModal: boolean;
  onClose: () => void;
  onCommit: (payload: CommitModalPayload) => void;
  checkedItemsCount: number;
  inProgress?: boolean;
  /**
   * Where the commit stands (`KuikaButton` `status`). Given, COMMIT carries it — "Committing",
   * then its ✓ or "Try again" — and `inProgress` no longer draws on it.
   */
  status?: KuikaButtonStatus;
}

const COMMIT_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Committing", success: "Committed" };

const CommitToBranchModal: React.FC<ICommitToBranchModalProps> = ({
  showModal,
  onClose,
  onCommit,
  checkedItemsCount,
  inProgress = false,
  status
}) => {
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");

  const handleSummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSummary(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const handleCommit = () => {
    onCommit({
      title: summary,
      message: description,
      commitMessage: buildCommitMessage(summary, description)
    });
  };

  const footer = (
    <KModalFooter
      onCancel={onClose}
      submitLabel="Commit"
      submitTestId="commitToBranchSubmitButton"
      onSubmit={handleCommit}
      loading={inProgress}
      status={status}
      statusLabels={COMMIT_STATUS_LABELS}
      disabled={!summary.trim()}
    />
  );

  return (
    <KModal showModal={showModal} closeModal={onClose} header="Commit to Branch" footer={footer} width={480} centered>
      <div className="kuika-branching-modal">
        <div className="kuika-branching-modal__header">
          <KuikaModalIconWrapper iconName="git-commit" intent="create" />
          <div className="kuika-branching-modal__title">
            <KuikaH5>Commit to Branch</KuikaH5>
          </div>
        </div>
        <div className="kuika-branching-modal__content">
          <div className="kuika-branching-modal__description">
            <KuikaText>Type down the summary and the description before merging the branch</KuikaText>
          </div>
          <div className="kuika-branching-modal__form">
            <div className="kuika-branching-modal__form-item">
              <KuikaInput
                data-testid="commitToBranchSummaryInput"
                placeholder="Summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                disabled={inProgress}
                autoFocus
              />
            </div>
            <div className="kuika-branching-modal__form-item">
              <KuikaInput
                type="textarea"
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={inProgress}
              />
            </div>
            <div className="kuika-branching-modal__branch-name">
              <KuikaText>
                {checkedItemsCount} {checkedItemsCount === 1 ? "item" : "items"} will be committed
              </KuikaText>
            </div>
          </div>
        </div>
      </div>
    </KModal>
  );
};

export default CommitToBranchModal;
